import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    PlayerWrapper,
    PlayerHeaderWrapper,
    PlayerNameWrapper,
    PlayerStatsWrapper,
    TwoWayLabel,
    PlayerStoryActionShot,
    HeadshotSkeletonWrapper
} from './TopPerformers.style';
import Skeleton from '@mlb/react-patterns/v4/components/Skeleton/skeleton';
import { resourcePath } from '@gameday/utils/resource-path';
import Link from '@mlb/react-patterns/v4/components/Link/link';
import Headshot from '@mlb/react-patterns/v4/components/Headshot/headshot';
import Card from '../Card/Card';
import { useSelector } from 'react-redux';
import { getContextData } from '@gameday/utils/selectors/getContextData';
import buildPlayerUrl from '@gameday/utils/buildPlayerUrl';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';

const actionFallback = 'https://img.mlbstatic.com/mlb-photos/image/upload/w_128,f_jpg/v1/people/generic/action/vertical/current';
const actionFallbackPath = 'd_people:generic:action:vertical:current.jpg/';
// Use this for determining which order to display the bottom stats line for hitters
const HITS_STATS_ORDER = [
    'homeRuns',
    'triples',
    'doubles',
    'baseOnBalls',
    'hitByPitch',
    'rbi',
    'runs',
    'stolenBases'
];

const PITCHING_STATS_ORDER = [
    'hits',
    'earnedRuns',
    'strikeOuts'
];

const dictionaryKeys = {
    homeRuns: 'labelBrief_h_hr',
    triples: 'labelBrief_t',
    doubles: 'labelBrief_d',
    baseOnBalls: 'labelBrief_h_bb',
    hitByPitch: 'labelBrief_hbp',
    rbi: 'labelBrief_rbi',
    runs: 'labelBrief_h_r',
    stolenBases: 'labelBrief_h_sb',
    inningsPitched: 'labelBrief_ip',
    hits: 'labelBrief_h_h',
    earnedRuns: 'labelBrief_er',
    strikeOuts: 'labelBrief_p_so',
    wins: 'win_abbrev',
    losses: 'loss_abbrev',
    saves: 'save_abbrev'
};
const THREE_X = '3x';
const TWO_X = '2x';
const ONE_X = '1x';
const widthString = {
    [THREE_X]: 'w_192',
    [TWO_X]: 'w_128',
    [ONE_X]: 'w_64'
};
const actionShotOptsPath = (scale) => actionFallbackPath + widthString[scale] + ',f_jpg/';
const getActionShotSrcSet = playerId => {
    return {
        [THREE_X]: resourcePath.get('playerActionShot', { optionsPath: actionShotOptsPath(THREE_X), playerId }),
        [TWO_X]: resourcePath.get('playerActionShot', { optionsPath: actionShotOptsPath(TWO_X), playerId }),
        [ONE_X]: resourcePath.get('playerActionShot', { optionsPath: actionShotOptsPath(ONE_X), playerId })
    };
};
const ProfilePhoto = ({ id, headshotType, size, showStory, isLoadingStories }) => {
    const srcSet = getActionShotSrcSet(id);
    return (
        isLoadingStories
            ? <HeadshotSkeletonWrapper>
                <Skeleton isCircle width={size} height={size} />
            </HeadshotSkeletonWrapper>
            : showStory
                ? <PlayerStoryActionShot
                    src={srcSet[THREE_X]}
                    srcset={srcSet}
                    ratio={'2:3'}
                    rounded={false}
                    size={size}
                    errorImageSrc={ actionFallback }
                />
                : <Headshot
                    width={size}
                    height={size}
                    playerId={ id }
                    type={headshotType}
                    zoomScale={1}
                    ratio='1:1'
                />);
};
ProfilePhoto.propTypes = {
    id: PropTypes.number,
    headshotType: PropTypes.string,
    size: PropTypes.string,
    showStory: PropTypes.bool,
    isLoadingStories: PropTypes.bool
};

export const PlayerSkeleton = ({ iteration }) => {
    return <PlayerWrapper key={`top-performer-${iteration}`}>
        <div>
            <Skeleton isCircle width='64px' height='64px' />
        </div>
        <Skeleton width='162px' height='64px' margin='0px' />
    </PlayerWrapper>;
};
PlayerSkeleton.propTypes = {
    iteration: PropTypes.number
};
const getLabeledStat = (translate) => {
    return (statNumber, statType, pitchingStat = false) => {
        if (isNaN(statNumber)) { // if NaN, assume stat is already labeled (ex. 5.0 IP)
            return statNumber;
        }
        const label = translate(dictionaryKeys[statType], '');
        const labeledStat = (statNumber <= 1 && !pitchingStat) ? label : statNumber + ' ' + label;
        return labeledStat;
    };
};

const getDecisionLabel = (translate) => {
    return (wins, losses, saves) => {
        if (typeof wins === 'string') {
            return wins + losses + saves;
        }
        const decisions = { wins, losses, saves };
        const decision = Object.keys(decisions).reduce((a, b) => decisions[a] > decisions[b] ? a : b);
        if (decisions[decision] === 0) {
            return '';
        };
        return translate(dictionaryKeys[decision]);
    };
};

const TopPerformersPlayer = ({
    player,
    iteration,
    language,
    deviceInteraction,
    tracking,
    isMobile,
    headshotSize,
    showStory,
    isLoadingStories = false,
    condensedStatLine
}) => {
    const translate = useTranslate();
    const getPlayerContextData = useSelector(getContextData);
    const { headshotType, disablePlayerLinks, playerPageContext } = getPlayerContextData(player.id);
    const labelStat = getLabeledStat(translate);
    const labelDecision = getDecisionLabel(translate);
    const {
        boxscoreName,
        id,
        isTwoWay,
        gameInfo: {
            box: {
                hitting,
                pitching
            } = {}
        } = {},
        type
    } = player;
    const url = showStory ? player.story : buildPlayerUrl({ nameSlug: id, language, playerPageContext });
    const twoWayExpandedStats = isTwoWay && !condensedStatLine; 

    const handlePlayerPage = useCallback((e) => {
        const activity = 'Top Performers Click';
        const cfg = {
            componentName: ['Top Performers'],
            text: activity,
            activity,
            linkName: `${activity}: ${id}`
        };
        tracking.track.track_click(cfg);
        if (deviceInteraction.playercard({ title: 'Player Card', url, pid: id })) {
            e.preventDefault();
            return false;
        }
    }, [url, id]);

    const handlers = {
        onClick: handlePlayerPage
    };

    let topLineStats = '';
    let bottomLineStats = '';
    if (type === 'hitter' || isTwoWay) {
        const {
            hits,
            atBats
        } = hitting;
        const secondLineStats = [];
        for (let i = 0; i < HITS_STATS_ORDER.length; i++) {
            const stat = hitting[HITS_STATS_ORDER[i]];
            if (stat) {
                const labeledStat = labelStat(stat, HITS_STATS_ORDER[i]);
                secondLineStats.push(labeledStat);
            }
        }
        const replacedHits = typeof hits === 'number' ? (hits + ' - ' + atBats) : hits.replace('-', ' - ');
        const joinedSecondLine = secondLineStats.join(', ');
        if (condensedStatLine) {
            topLineStats = replacedHits + ', ' + joinedSecondLine;
        } else {
            if (isTwoWay) {
                bottomLineStats = replacedHits + ', ' + joinedSecondLine;
            } else {
                topLineStats = replacedHits;
                bottomLineStats = joinedSecondLine;
            }
        }
    }

    if (type !== 'hitter' || isTwoWay) {
        const {
            wins = '',
            losses = '',
            saves = '',
            inningsPitched = ''
        } = pitching;
        const topStats = [];
        const decision = labelDecision(wins, losses, saves);
        if (decision) {
            topStats.push(decision);
        }
        topStats.push(labelStat(inningsPitched, 'inningsPitched'));

        const bottomStats = [];
        for (let i = 0; i < PITCHING_STATS_ORDER.length; i++) {
            const stat = pitching[PITCHING_STATS_ORDER[i]];
            if (stat !== null && stat !== undefined) {
                const labeledStat = labelStat(stat, PITCHING_STATS_ORDER[i], true);
                bottomStats.push(labeledStat);
            }
        }
        const topLineJoined = topStats.join(', ');
        const bottomLineJoined = bottomStats.join(', ');

        if (condensedStatLine) {
            if (isTwoWay) {
                bottomLineStats = topLineJoined + ', ' + bottomLineJoined;
            } else {
                topLineStats = topLineJoined + ', ' + bottomLineJoined;
            }
        } else {
            if (isTwoWay) {
                topLineStats = `${topLineJoined}, ${bottomLineJoined}`;
            } else {
                topLineStats = topLineJoined;
                bottomLineStats = bottomLineJoined;
            }
        }
    }

    const playerEl = <PlayerWrapper key={`top-performer-${iteration}`}>
        <div>
            {
                !isLoadingStories && (!disablePlayerLinks || showStory)
                    ? (
                        <Link
                            url={ url }
                            handlers={ handlers }
                            target='_blank'
                        >
                            <ProfilePhoto
                                id={id}
                                headshotType={headshotType}
                                size={headshotSize}
                                showStory={showStory}
                                isLoadingStories={isLoadingStories}
                            />
                        </Link>
                    )
                    : (
                        <ProfilePhoto
                            id={id}
                            headshotType={headshotType}
                            size={headshotSize}
                            showStory={showStory}
                            isLoadingStories={isLoadingStories}
                        />
                    )
            }
        </div>
        <div>
            <PlayerHeaderWrapper>
                {!disablePlayerLinks
                    ? (
                        <PlayerNameWrapper
                            url={ url }
                            handlers={ handlers }
                            target='_blank'
                        >
                            {boxscoreName}
                        </PlayerNameWrapper>
                    )
                    : (
                        <PlayerNameWrapper as='span' $noHover={true}>
                            {boxscoreName}
                        </PlayerNameWrapper>
                    )}
            </PlayerHeaderWrapper>
            <PlayerStatsWrapper>
                { twoWayExpandedStats ? <TwoWayLabel>P</TwoWayLabel> : null }
                {topLineStats}
            </PlayerStatsWrapper>
            { bottomLineStats && (
                <PlayerStatsWrapper>
                    { twoWayExpandedStats ? <TwoWayLabel>B</TwoWayLabel> : null}
                    {bottomLineStats}
                </PlayerStatsWrapper>
            )}
        </div>
    </PlayerWrapper>;

    return isMobile
        ? <Card>
            {playerEl}
        </Card>
        : playerEl;
};

TopPerformersPlayer.propTypes = {
    player: PropTypes.object,
    iteration: PropTypes.number,
    isLoading: PropTypes.bool,
    deviceInteraction: PropTypes.object,
    language: PropTypes.string,
    isMobile: PropTypes.bool,
    tracking: PropTypes.object,
    headshotSize: PropTypes.string,
    showStory: PropTypes.bool,
    isLoadingStories: PropTypes.bool,
    condensedStatLine: PropTypes.bool
};

export default TopPerformersPlayer;
